import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * Options pour la configuration du hook useFilters
 */
interface UseFiltersOptions<T> {
  /**
   * Fonction de transformation des filtres avant la sérialisation
   * Utile pour transformer des objets complexes ou dates
   */
  transformForSerialization?: (filters: Partial<T>) => any;
  
  /**
   * Fonction de transformation des filtres après la désérialisation
   * Utile pour reconstituer des objets complexes ou dates
   */
  transformForDeserialization?: (filters: any) => Partial<T>;
  
  /**
   * Fonction pour transformer les filtres avant l'affichage
   * Utile pour mapper ou aplatir des propriétés imbriquées
   */
  transformForDisplay?: (filters: Partial<T>) => Record<string, any>;
}

/**
 * Résultat retourné par le hook useFilters
 */
interface UseFiltersResult<T> {
  /**
   * État actuel des filtres
   */
  filters?: Partial<T>;
  
  /**
   * Fonction pour définir les filtres (utilisée dans handleFilterSubmit)
   */
  setFilters: React.Dispatch<React.SetStateAction<Partial<T> | undefined>>;
  
  /**
   * Fonction pour effacer un filtre spécifique ou mettre à jour sa valeur
   */
  clearFilter: (field: string, newValue?: any) => Promise<void>;
  
  /**
   * Fonction pour effacer tous les filtres
   */
  clearAllFilters: () => Promise<void>;
  
  /**
   * Fonction de gestion de la soumission du formulaire de filtres
   */
  handleFilterSubmit: (filterData: Partial<T>) => Promise<void>;
  
  /**
   * Filtres transformés pour l'affichage (si transformForDisplay est fourni)
   */
  displayFilters: Record<string, any>;
  
  /**
   * Indicateur d'initialisation des filtres
   */
  isInitialized: boolean;
}

/**
 * Vérifier si deux objets sont égaux (comparaison superficielle)
 */
const shallowEqual = (obj1: any, obj2: any): boolean => {
  if (obj1 === obj2) return true;
  if (!obj1 || !obj2) return false;
  
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);
  
  if (keys1.length !== keys2.length) return false;
  
  for (const key of keys1) {
    if (obj1[key] !== obj2[key]) return false;
  }
  
  return true;
};

/**
 * Hook pour gérer les filtres avec persistance dans l'URL
 * @param resetPageCallback - Fonction pour réinitialiser la pagination
 * @param options - Options de configuration
 * @returns - Interface pour manipuler les filtres
 */
export function useFilters<T extends object>(
  resetPageCallback: () => void,
  options: UseFiltersOptions<T> = {}
): UseFiltersResult<T> {
  const [filters, setFilters] = useState<Partial<T>>();
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Référence pour stocker la dernière valeur serialisée des filtres
  const lastSerializedFiltersRef = useRef<string>("");
  
  const location = useLocation();
  const navigate = useNavigate();

  // Mémoriser les fonctions de transformation pour éviter les recréations inutiles
  const transformForSerialization = useMemo(() => options.transformForSerialization, [options.transformForSerialization]);
  const transformForDeserialization = useMemo(() => options.transformForDeserialization, [options.transformForDeserialization]);
  const transformForDisplay = useMemo(() => options.transformForDisplay, [options.transformForDisplay]);

  // Fonction pour sérialiser les filtres en paramètres d'URL
  const serializeFilters = useCallback((filtersToSerialize: Partial<T>): string => {
    if (!filtersToSerialize || Object.keys(filtersToSerialize).length === 0) return "";
    try {
      // Copie profonde pour éviter de modifier l'objet original
      let filtersCopy = JSON.parse(JSON.stringify(filtersToSerialize));
      
      // Appliquer la transformation si elle existe
      if (transformForSerialization) {
        filtersCopy = transformForSerialization(filtersCopy);
      }
      
      return encodeURIComponent(JSON.stringify(filtersCopy));
    } catch (error) {
      console.error("Erreur lors de la sérialisation des filtres:", error);
      return "";
    }
  }, [transformForSerialization]);
  
  // Fonction pour désérialiser les paramètres d'URL en filtres
  const deserializeFilters = useCallback((queryParam: string): Partial<T> => {
    if (!queryParam) return {};
    
    try {
      let decodedFilters = JSON.parse(decodeURIComponent(queryParam));
      
      // Appliquer la transformation si elle existe
      if (transformForDeserialization) {
        decodedFilters = transformForDeserialization(decodedFilters);
      }
      return decodedFilters;
    } catch (error) {
      console.error("Erreur lors de la désérialisation des filtres:", error);
      return {};
    }
  }, [transformForDeserialization]);

  // Récupérer les filtres depuis l'URL au chargement initial
  useEffect(() => {
    if (isInitialized) return;
    
    const searchParams = new URLSearchParams(location.search);
    let filtersParam = searchParams.get("filters");
    
    if (!filtersParam) filtersParam = "{}";

    const deserializedFilters = deserializeFilters(filtersParam);
    setFilters(deserializedFilters);
    lastSerializedFiltersRef.current = filtersParam;
    
    setIsInitialized(true);
  }, [location.search, isInitialized, deserializeFilters]);
  
  // Mettre à jour l'URL quand les filtres changent
  useEffect(() => {
    if (!isInitialized || !filters) return;
    
    const serializedFilters = serializeFilters(filters);
    
    // Éviter les mises à jour inutiles en comparant avec la dernière valeur serialisée
    if (serializedFilters === lastSerializedFiltersRef.current) {
      return;
    }
    
    lastSerializedFiltersRef.current = serializedFilters;
    
    const searchParams = new URLSearchParams(location.search);
    
    if (serializedFilters) {
      searchParams.set("filters", serializedFilters);
    } else {
      searchParams.delete("filters");
    }
    
    navigate({
      pathname: location.pathname,
      search: searchParams.toString()
    }, { replace: true });
    
  }, [filters, isInitialized, location.pathname, location.search, navigate, serializeFilters]);

  // Gestion de la soumission du formulaire de filtres
  const handleFilterSubmit = useCallback((filterData: Partial<T>): Promise<void> => {
    return new Promise<void>((resolve) => {
      // Éviter les mises à jour inutiles si les filtres n'ont pas changé
      setFilters(prevFilters => {
        if (shallowEqual(prevFilters, filterData)) {
          return prevFilters;
        }
        resetPageCallback();
        return filterData || {};
      });
      resolve();
    });
  }, [resetPageCallback]);

  // Supprimer un filtre spécifique ou mettre à jour sa valeur
  const clearFilter = useCallback((field: string, newValue?: any): Promise<void> => {
    return new Promise<void>((resolve) => {
      setFilters(currentFilters => {
        if (!currentFilters) return undefined;

        let newFilters: Partial<T>;
        
        if (newValue === null || newValue === undefined) {
          // Supprimer le filtre
          newFilters = { ...currentFilters };
          
          // Gérer les propriétés imbriquées avec la notation par points
          if (field.includes(".")) {
            const [parent, child] = field.split(".");
            if (newFilters[parent as keyof typeof newFilters]) {
              const parentObj = { ...newFilters[parent as keyof typeof newFilters] as Record<string, any> };
              delete parentObj[child];
              
              if (Object.keys(parentObj).length === 0) {
                delete newFilters[parent as keyof typeof newFilters];
              } else {
                newFilters[parent as keyof typeof newFilters] = parentObj as any;
              }
            }
          } else {
            delete newFilters[field as keyof typeof newFilters];
          }
        } else {
          // Mettre à jour la valeur
          if (field.includes(".")) {
            const [parent, child] = field.split(".");
            newFilters = {
              ...currentFilters,
              [parent]: {
                ...(currentFilters[parent as keyof typeof currentFilters] || {}),
                [child]: newValue
              }
            };
          } else {
            newFilters = {
              ...currentFilters,
              [field]: newValue
            };
          }
        }
        
        // Éviter les mises à jour inutiles si les filtres n'ont pas changé
        if (shallowEqual(currentFilters, newFilters)) {
          return currentFilters;
        }
        
        resetPageCallback();
        return newFilters;
      });
      
      resolve();
    });
  }, [resetPageCallback]);

  // Supprimer tous les filtres
  const clearAllFilters = useCallback((): Promise<void> => {
    return new Promise<void>((resolve) => {
      setFilters(currentFilters => {
        if (!currentFilters || Object.keys(currentFilters).length === 0) {
          return currentFilters;
        }
        resetPageCallback();
        return {};
      });
      resolve();
    });
  }, [resetPageCallback]);

  // Calculer les filtres pour l'affichage
  const displayFilters = transformForDisplay && filters
    ? transformForDisplay(filters) 
    : filters as unknown as Record<string, any>;

  return {
    filters,
    setFilters,
    clearFilter,
    clearAllFilters,
    handleFilterSubmit,
    displayFilters,
    isInitialized
  };
} 